import React, { useRef, useEffect } from 'react';
import Autocomplete from 'apollo-react/components/Autocomplete';
import Info from 'apollo-react-icons/Info';
import { createEmailObject } from '../../utils/userUtils';
import styles from './UserSelectionBase.module.css';

/**
 * Base User Selection Component
 * Reusable component for selecting users with support for typing emails
 *
 * @param {string} label - Field label
 * @param {boolean} required - Whether field is required
 * @param {boolean} multiple - Allow multiple selections
 * @param {Array|Object} value - Selected value(s)
 * @param {Array} suggestions - Filtered suggestions
 * @param {Function} completeMethod - Search handler
 * @param {Function} onChange - Change handler
 * @param {string} placeholder - Placeholder text
 * @param {Object} errors - Validation errors
 * @param {boolean} disabled - Disable the field
 * @param {boolean} enableKeyboardAdd - Enable Enter/Space to add typed emails
 */
export default function UserSelectionBase({
  label,
  required = false,
  multiple = false,
  value,
  suggestions,
  completeMethod, // eslint-disable-line no-unused-vars
  onChange,
  placeholder,
  errors,
  disabled = false,
  enableKeyboardAdd = false,
}) {
  const autocompleteRef = useRef(null);
  // format suggestions to match apollo-react autocomplete format
  const formattedSuggestions =
    suggestions?.map((user) => ({
      label: user.username || user.email || user,
      key: user.id || user.username || user.email || user,
      ...user,
    })) || [];

  // format value to match apollo-react autocomplete format
  const formattedValue = multiple
    ? Array.isArray(value)
      ? value.map((v) => ({
          label: v.username || v.email || v,
          key: v.id || v.username || v.email || v,
          ...v,
        }))
      : []
    : value
      ? {
          label: value.username || value.email || value,
          key: value.id || value.username || value.email || value,
          ...value,
        }
      : null;

  const handleChange = (event, newValue) => {
    // convert back to original format
    if (multiple) {
      const originalValue = Array.isArray(newValue)
        ? newValue.map((v) => {
            // preserve email string format or user object format
            if (v.email && !v.id) {
              return v.email;
            }
            return v;
          })
        : [];
      onChange({ value: originalValue });
    } else {
      const originalValue = newValue
        ? newValue.email && !newValue.id
          ? newValue.email
          : newValue
        : null;
      onChange({ value: originalValue });
    }
  };

  // add keyboard handler for Enter and Space keys (only if enabled and multiple)
  useEffect(() => {
    if (enableKeyboardAdd && multiple && autocompleteRef.current) {
      // apollo-react Autocomplete wraps the input, we need to find it
      const inputElement =
        autocompleteRef.current.querySelector('input[type="text"]');

      if (inputElement) {
        const handleKeyDown = (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target.value.trim()) {
            e.preventDefault();
            e.stopPropagation();
            const typedValue = e.target.value.trim();

            // Wrap email string in an object so AutoComplete can display it
            const emailObject = createEmailObject(typedValue);

            // Add the email object to selections
            const newSelections = Array.isArray(value)
              ? [...value, emailObject]
              : [emailObject];
            onChange({ value: newSelections });

            // Clear the input
            setTimeout(() => {
              e.target.value = '';
            }, 0);
          }
        };

        inputElement.addEventListener('keydown', handleKeyDown);
        return () => inputElement.removeEventListener('keydown', handleKeyDown);
      }
    }
  }, [enableKeyboardAdd, multiple, value, onChange]);

  return (
    <div ref={autocompleteRef}>
      <Autocomplete
        label={
          <span>
            {label} {<Info fontSize="extraSmall" className={styles.infoIcon} />}
          </span>
        }
        placeholder={placeholder}
        helperText={errors || ''}
        error={!!errors}
        source={formattedSuggestions}
        fullWidth
        required={required}
        value={formattedValue}
        multiple={multiple}
        disabled={disabled}
        onChange={handleChange}
      />
    </div>
  );
}
